/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Add or edit an eater.
 *
 * The web counterpart of iOS's DietProfileEditor. Until now the web could show
 * you that someone was allergic to peanuts but gave you no way to say so — the
 * list was read-only, so the single most safety-relevant setting in the app was
 * only enterable from a phone.
 *
 * ── SOFT VERSUS HARD ────────────────────────────────────────────────────────
 * `allergens` is a HARD constraint. The server enforces it in code after every
 * AI call, over every generated recipe, and flags conflicts across the whole
 * meal plan. `dietTags` and `dislikes` are SOFT — they steer the model and
 * nothing more. The two are visually separated and labelled, because a user who
 * believes "vegetarian" is enforced the way "peanut" is has been misled by the
 * interface.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { CatalogApi, SettingsApi } from "../../api/endpoints.ts";
import type { DietProfile } from "../../api/types.ts";
import { Modal, Pill, Spinner } from "../../components/ui.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";

function toggle(list: string[], tag: string): string[] {
  return list.includes(tag) ? list.filter((x) => x !== tag) : [...list, tag];
}

/** Comma-separated free text ↔ a lower-cased tag array. */
function parseTags(text: string): string[] {
  return text.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
}

export function DietProfileEditor({ existing, onClose, onSaved }: {
  existing?: DietProfile;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const { lang } = usePrefs();
  const [name, setName] = useState(existing?.name ?? "");
  const [allergens, setAllergens] = useState<string[]>(existing?.allergens ?? []);
  const [dietTags, setDietTags] = useState<string[]>(existing?.diet_tags ?? []);
  const [portion, setPortion] = useState(existing?.counts_as_servings ?? 1);
  const [saving, setSaving] = useState(false);
  const [dislikeText, setDislikeText] = useState((existing?.dislikes ?? []).join(", "));

  // The lists come from the server. They used to be hardcoded here AND in the
  // iOS editor AND in ALLERGEN_KEYWORDS; only the last is what the gate matches
  // on, and these two had drifted from it — both offered "treenuts" while the
  // real key is "tree nuts", so a tick here bought no enforcement at all.
  const catalog = useQuery({
    queryKey: ["diet-options", lang],
    queryFn: () => CatalogApi.dietOptions(lang),
    staleTime: 60 * 60 * 1000,
  });

  const allergenOptions = catalog.data?.allergens ?? [];
  const dietOptions = catalog.data?.diets ?? [];
  const knownAllergen = new Set(allergenOptions.map((a) => a.key));
  const knownDiet = new Set(dietOptions.map((d) => d.key));

  // Anything already saved that the catalogue doesn't offer — a tag typed
  // before this list existed, or one a regulator has since dropped. Shown so it
  // is editable rather than silently discarded on the next save.
  const customAllergens = allergens.filter((a) => !knownAllergen.has(a));
  const customDiets = dietTags.filter((d) => !knownDiet.has(d));

  // Allergens a strict follower of the chosen diets also avoids, that are not
  // already ticked. Advisory: offered as a prompt, never applied on its own.
  const impliedMissing = [...new Set(
    dietOptions
      .filter((d) => dietTags.includes(d.key))
      .flatMap((d) => d.impliesAvoid),
  )].filter((k) => !allergens.includes(k));

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const body = {
      name: name.trim(),
      allergens: [...new Set(allergens)],
      dietTags: [...new Set(dietTags)],
      dislikes: parseTags(dislikeText),
      countsAsServings: portion,
    };
    try {
      if (existing) await SettingsApi.updateDiet(existing.id, body);
      else await SettingsApi.addDiet(body);
      toast(existing ? "Saved" : `${body.name} added`, "ok");
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Couldn't save.", "danger");
    }
    setSaving(false);
  };

  return (
    <Modal title={existing ? `Edit ${existing.name}` : "Add someone"} onClose={onClose} wide>
      <div className="space-y-5">
        <label className="block">
          <span className="text-sm font-medium">Name</span>
          <input className="input mt-1" placeholder="Who is this?"
                 value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>

        {/* ── Hard constraint ──────────────────────────────────────────── */}
        <fieldset className="rounded-xl2 border border-danger/30 bg-danger/5 p-4">
          <legend className="px-1 text-sm font-medium text-danger">Allergies</legend>
          <p className="mb-3 text-xs text-ink-2">
            Enforced. Every generated recipe is checked against this list in code,
            and the meal plan flags any recipe containing one. This is not a
            preference.
          </p>
          {catalog.isLoading ? <Spinner /> : (
            <div className="flex flex-wrap gap-2">
              {allergenOptions.map((opt) => (
                <button key={opt.key} type="button"
                        title={opt.description}
                        onClick={() => setAllergens((a) => toggle(a, opt.key))}
                        className={`chip transition ${
                          allergens.includes(opt.key)
                            ? "border-danger bg-danger/15 text-danger"
                            : "hover:border-danger/40"}`}>
                  {allergens.includes(opt.key) && <span aria-hidden>✓ </span>}{opt.label}
                </button>
              ))}
              {customAllergens.map((tag) => (
                <button key={tag} type="button"
                        title="Added before this list existed"
                        onClick={() => setAllergens((a) => toggle(a, tag))}
                        className="chip border-danger bg-danger/15 text-danger">
                  ✓ {tag}
                </button>
              ))}
            </div>
          )}
          {/* Hovering a chip explains what it covers — "soy" catching tofu and
              miso is not obvious, and picking the wrong one is the failure that
              matters here. */}
          {allergens.length > 0 && (
            <p className="mt-3 text-xs text-ink-2">
              {allergenOptions
                .filter((o) => allergens.includes(o.key))
                .map((o) => o.description)
                .join(" ")}
            </p>
          )}
        </fieldset>

        {/* ── Soft preferences ─────────────────────────────────────────── */}
        <fieldset className="rounded-xl2 border border-rule-soft p-4">
          <legend className="px-1 text-sm font-medium">Diet</legend>
          <p className="mb-3 text-xs text-ink-3">
            A preference. It steers what the AI suggests; it is not enforced.
          </p>
          {catalog.isLoading ? <Spinner /> : (
            <div className="space-y-3">
              {(["exclusion", "preference", "medical"] as const).map((kind) => {
                const group = dietOptions.filter((d) => d.kind === kind);
                if (group.length === 0) return null;
                return (
                  <div key={kind}>
                    <div className="label-mono mb-1.5">
                      {kind === "exclusion" ? "Excludes foods"
                        : kind === "medical" ? "Clinician-directed"
                        : "Preferences"}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {group.map((opt) => (
                        <button key={opt.key} type="button"
                                title={opt.description}
                                onClick={() => setDietTags((d) => toggle(d, opt.key))}
                                className={`chip transition ${
                                  dietTags.includes(opt.key)
                                    ? "border-accent bg-accent/15 text-accent"
                                    : "hover:border-accent/40"}`}>
                          {dietTags.includes(opt.key) && <span aria-hidden>✓ </span>}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {customDiets.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {customDiets.map((tag) => (
                    <button key={tag} type="button"
                            onClick={() => setDietTags((d) => toggle(d, tag))}
                            className="chip border-accent bg-accent/15 text-accent">
                      ✓ {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* A vegan who eats dairy by mistake is disappointed; someone
              dairy-allergic is in danger. The nudge offers the upgrade and
              never makes it silently. */}
          {impliedMissing.length > 0 && (
            <div className="mt-3 rounded-lg border border-warn/40 bg-warn/5 p-2.5 text-xs">
              <span className="text-ink-2">
                Should {name.trim() || "this person"} also have{" "}
                {impliedMissing.join(", ")} as an <strong>allergy</strong>? Only if
                it is one — a diet is a choice, an allergy is enforced.
              </span>
              <button className="btn-ghost ml-2 !px-2 !py-0.5 text-xs"
                      onClick={() => setAllergens((a) => [...new Set([...a, ...impliedMissing])])}>
                Add
              </button>
            </div>
          )}

          <input className="input mt-3" placeholder="Dislikes, comma separated"
                 value={dislikeText} onChange={(e) => setDislikeText(e.target.value)} />
          <p className="mt-1.5 text-xs text-ink-3">Avoided where possible.</p>
        </fieldset>

        <label className="block">
          <span className="text-sm font-medium">Appetite</span>
          <div className="mt-1 flex items-center gap-3">
            <input type="range" min={0} max={2} step={0.25} value={portion}
                   className="flex-1"
                   onChange={(e) => setPortion(Number(e.target.value))} />
            <Pill tone="neutral">{portion}× portion</Pill>
          </div>
          {/* A six-year-old eats half an adult portion, and a plan that pretends
              otherwise buys too much food every week. */}
          <p className="mt-1.5 text-xs text-ink-3">
            How much of an adult portion this person eats. A six-year-old is
            about half.
          </p>
        </label>

        <div className="flex justify-end gap-2 border-t border-rule-soft pt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={saving || !name.trim()} onClick={() => void save()}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
